import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import dayjs from "dayjs";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";
import { CustomAddressAutocomplete } from "../../shared/CustomAddressAutoComplete";
import SecondOwnerBirthday from "../../questionscomponent/subcomponent/SecondOwnerBirthday";

const CITIZENSHIP_OPTIONS = ["US Citizen", "Permanent Resident", "Resident Alien", "Other"];

function formatDashed(sanitizedValue, dashIndexes) {
  let formatted = "";
  for (let i = 0; i < sanitizedValue.length; i++) {
    formatted += dashIndexes.includes(i) ? `-${sanitizedValue[i]}` : sanitizedValue[i];
  }
  return formatted;
}

function formatPhoneNumber(digitsOnly) {
  if (!digitsOnly) return "";
  if (digitsOnly.length <= 3) return `(${digitsOnly}`;
  if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3)}`;
  return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
}

function makeEmptyOwnerRow(order) {
  return {
    order,
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    // No default date — a new owner's DOB starts empty until actually
    // picked, not silently pre-filled with today.
    dob: null,
    ssn: "",
    ownershipPercentage: "",
    homeAddress: "",
    homeAddressComponents: [],
    homeAddressError: "",
    unitNumber: "",
  };
}

/**
 * PD-6 Milestone 2 — business path Step 3 "Ownership".
 *
 * Merges Ssn.js (SSN + citizenship), taxId.js and Ownership.js (numeric
 * ownership %) into one step. When ownership % is entered below 100, one or
 * more additional-owner rows (email/phone/name/DOB/SSN/home address/share)
 * are revealed inline instead of routing to separate screens — "Add
 * another owner" appends more rows for a 3rd, 4th, etc. owner, and their
 * shares must sum to exactly the remaining percentage before Continue
 * enables.
 *
 * Every field keeps its own individual `partial-application` autosave POST
 * call with the exact same payload shape as its original standalone screen,
 * except the owner rows themselves, which post as a single
 * `additional_owners` array (see PartialApplicationCreateOrUpdateView
 * .return_additional_owners_response on the backend) — the stepper always
 * sends its complete current owner list there rather than one field at a
 * time, since the list can grow and shrink.
 */
const StepOwnership = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const [ssn, setSsn] = useState(() => formatDashed(state?.questions?.ssn ?? "", [3, 5]));
  const [citizenship, setCitizenship] = useState(state?.questions?.citizenship ?? "");
  const [taxId, setTaxId] = useState(() =>
    formatDashed(state?.questions?.business?.taxId ?? "", [2])
  );

  const [ownershipPercentage, setOwnershipPercentage] = useState(
    state?.questions?.business?.ownershipPercentage ?? 100
  );

  const [additionalOwners, setAdditionalOwners] = useState(() => {
    const saved = state?.questions?.business?.additionalOwners;
    if (!Array.isArray(saved) || saved.length === 0) return [];
    return saved.map((owner) => ({
      order: owner.order,
      email: owner.email ?? "",
      phone: formatPhoneNumber((owner.phone ?? "").replace("+1", "").replace(/[^0-9]/g, "")),
      firstName: owner.firstName ?? "",
      lastName: owner.lastName ?? "",
      dob: owner.dob ? dayjs(owner.dob) : null,
      ssn: formatDashed((owner.ssn ?? "").replace(/-/g, ""), [3, 5]),
      ownershipPercentage: owner.ownershipPercentage ?? "",
      homeAddress: owner.homeAddress ?? "",
      homeAddressComponents: owner.homeAddressComponents ?? [],
      homeAddressError: "",
      unitNumber: owner.unitNumber ?? "",
    }));
  });

  // Selecting a suggestion fills in the address text immediately, but its
  // structured components (required for validation) only land once the
  // place-details lookup resolves. Track in-flight lookups so Continue can't
  // race ahead of them.
  const [pendingAddressLookups, setPendingAddressLookups] = useState(0);
  const handleAddressResolvingChange = (isResolving) => {
    setPendingAddressLookups((count) => count + (isResolving ? 1 : -1));
  };

  const [errors, setErrors] = useState({});
  const [ownerRowErrors, setOwnerRowErrors] = useState({});

  // "Just saved now": every field on this step lands in global state as the
  // borrower types/selects, not only once they click Continue — so
  // navigating Back then Continue again restores whatever was filled in
  // here (including every additional-owner row), instead of losing it
  // because this component's local state was discarded on unmount. Owner
  // rows are stored in the same shape handleContinue's ownersForState
  // already produces, so the additionalOwners useState initializer above
  // (which expects that exact shape) can read them straight back.
  useEffect(() => {
    const sanitizedSsn = ssn.replace(/-/g, "");
    const sanitizedTaxId = taxId.replace(/-/g, "");
    const ownersForState = additionalOwners.map((owner) => ({
      order: owner.order,
      email: owner.email,
      phone: owner.phone ? "+1" + owner.phone.replace(/[^0-9]/g, "") : "",
      firstName: owner.firstName,
      lastName: owner.lastName,
      dob: owner.dob ? owner.dob.format("YYYY-MM-DD") : "",
      ssn: owner.ssn.replace(/-/g, ""),
      ownershipPercentage: owner.ownershipPercentage === "" ? "" : Number(owner.ownershipPercentage),
      homeAddress: owner.homeAddress,
      homeAddressComponents: owner.homeAddressComponents,
      unitNumber: owner.unitNumber,
    }));
    dispatch({
      type: "QUESTIONS",
      payload: { ...state.questions, ssn: sanitizedSsn, citizenship },
    });
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        taxId: sanitizedTaxId,
        ownershipPercentage,
        additionalOwners: ownersForState,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssn, citizenship, taxId, ownershipPercentage, additionalOwners]);

  // PD-22: client confirmed the "add another owner" section should disappear
  // entirely at 80%+ ownership (was previously required any time it wasn't
  // exactly 100%) — sending an empty additional_owners array below (see
  // handleContinue's `hasAdditionalOwners ? ... : []`) already clears out
  // any rows the applicant had added before raising their share past 80%.
  const hasAdditionalOwners = ownershipPercentage !== "" && Number(ownershipPercentage) < 80;
  // The full pool left for every owner besides the primary applicant — the
  // owner rows' shares must sum to exactly this before Continue enables.
  const remainingShare = ownershipPercentage === "" ? "" : 100 - Number(ownershipPercentage);
  const allocatedShare = additionalOwners.reduce(
    (sum, owner) => sum + (Number(owner.ownershipPercentage) || 0),
    0
  );
  // What's actually been committed across the whole business (primary +
  // every owner row so far) vs. what's still unclaimed of the full 100% —
  // this is what the "Remaining share" line below shows, since "remaining"
  // should shrink as owner rows are filled in, not stay pinned at the pool
  // size regardless of what's already been assigned.
  const totalAssignedShare = hasAdditionalOwners
    ? Number(ownershipPercentage) + allocatedShare
    : 0;
  const totalUnassignedShare = 100 - totalAssignedShare;

  // Seed one empty owner row the moment the share first drops below 100% —
  // same auto-reveal the old single-owner panel had. Only fires once: after
  // that, an empty list means the borrower deliberately removed every row,
  // and re-seeding on every render would make "Remove" impossible to use.
  const autoSeededRef = useRef(false);
  useEffect(() => {
    if (hasAdditionalOwners && additionalOwners.length === 0 && !autoSeededRef.current) {
      autoSeededRef.current = true;
      setAdditionalOwners([makeEmptyOwnerRow(1)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdditionalOwners]);

  const handleOwnershipChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly === "") return setOwnershipPercentage("");
    setOwnershipPercentage(Math.min(parseInt(digitsOnly, 10), 100));
  };

  const updateOwnerRow = (order, patch) => {
    setAdditionalOwners((rows) =>
      rows.map((row) => (row.order === order ? { ...row, ...patch } : row))
    );
  };

  const addOwnerRow = () => {
    setAdditionalOwners((rows) => [
      ...rows,
      makeEmptyOwnerRow((rows[rows.length - 1]?.order ?? 0) + 1),
    ]);
  };

  const removeOwnerRow = (order) => {
    // Re-number what's left so the first remaining row is always order 1 —
    // that's the row the backend mirrors into the legacy second-owner
    // fields that PDF generation and the Salesforce sync still read.
    setAdditionalOwners((rows) =>
      rows.filter((row) => row.order !== order).map((row, idx) => ({ ...row, order: idx + 1 }))
    );
  };

  const handleOwnerPhoneChange = (order, e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly.length <= 10) updateOwnerRow(order, { phone: formatPhoneNumber(digitsOnly) });
  };

  const handleOwnerSSNChange = (order, e) => {
    const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "");
    if (sanitized.length < 10) updateOwnerRow(order, { ssn: formatDashed(sanitized, [3, 5]) });
  };

  const validate = () => {
    const validationErrors = {};
    const rowErrors = {};
    if (!ssn || !/^\d{3}-\d{2}-\d{4}$/.test(ssn)) validationErrors.ssn = "Please provide a valid SSN e.g. (AAA-GG-SSSS)";
    if (!citizenship) validationErrors.citizenship = "Please select an option.";
    if (taxId && !/^[0-9A-Za-z]{2}-[0-9A-Za-z]{7}$/.test(taxId)) {
      validationErrors.taxId = "Please provide a valid Tax Id e.g. (xx-xxxxxxx)";
    }
    // The old range slider couldn't produce an empty or out-of-range value —
    // a free-text field can, so this needs to be enforced explicitly now.
    if (!ownershipPercentage || Number(ownershipPercentage) < 1 || Number(ownershipPercentage) > 100) {
      validationErrors.ownershipPercentage = "Enter a share between 1 and 100.";
    }
    if (hasAdditionalOwners) {
      if (additionalOwners.length === 0) {
        validationErrors.additionalOwners = "Add at least one additional owner.";
      }
      additionalOwners.forEach((owner) => {
        const errs = {};
        if (!owner.email) errs.email = "Required.";
        if (!owner.phone || !/^\(\d{3}\)-\d{3}-\d{4}$/.test(owner.phone)) {
          errs.phone = "Please provide a valid US phone number e.g. (234)-567-8900";
        }
        if (!owner.firstName || owner.firstName.trim().length < 3) {
          errs.firstName = "Please enter a valid first name (min 3 characters).";
        }
        if (!owner.lastName || owner.lastName.trim().length < 3) {
          errs.lastName = "Please enter a valid last name (min 3 characters).";
        }
        if (!owner.dob || !dayjs(owner.dob).isValid() || dayjs(owner.dob).isAfter(dayjs())) {
          errs.dob = "Please choose a valid date.";
        }
        if (!owner.ssn || !/^[0-9A-Za-z]{3}-[0-9A-Za-z]{2}-[0-9A-Za-z]{4}$/.test(owner.ssn)) {
          errs.ssn = "Please provide a valid SSN e.g. (AAA-GG-SSSS)";
        }
        if (!owner.homeAddress || owner.homeAddressComponents.length === 0) {
          errs.homeAddress = "Please select an address from the list.";
        }
        if (
          !owner.ownershipPercentage ||
          Number(owner.ownershipPercentage) < 1 ||
          Number(owner.ownershipPercentage) > 100
        ) {
          errs.ownershipPercentage = "Enter a share between 1 and 100.";
        }
        if (Object.keys(errs).length > 0) rowErrors[owner.order] = errs;
      });
      if (additionalOwners.length > 0 && allocatedShare !== Number(remainingShare)) {
        validationErrors.ownerShareSum = `Owner shares must add up to exactly 100% — currently ${totalAssignedShare}%.`;
      }
    }
    return { validationErrors, rowErrors };
  };

  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      const { validationErrors, rowErrors } = validate();
      setErrors(validationErrors);
      setOwnerRowErrors(rowErrors);
      setSubmitAttempted(true);
      // Always set (not just on failure), so a row whose address was just
      // fixed has its stale error cleared instead of staying stuck.
      additionalOwners.forEach((owner) => {
        updateOwnerRow(owner.order, { homeAddressError: rowErrors[owner.order]?.homeAddress || "" });
      });
      return Object.keys(validationErrors).length === 0 && Object.keys(rowErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press. Guards each owner row's homeAddressError update
  // against reassigning an unchanged value — updateOwnerRow creates a new
  // `additionalOwners` array reference, which is itself a dependency below,
  // so an unconditional set here would re-trigger this same effect forever.
  useEffect(() => {
    if (!submitAttempted) return;
    const { validationErrors, rowErrors } = validate();
    setErrors(validationErrors);
    setOwnerRowErrors(rowErrors);
    additionalOwners.forEach((owner) => {
      const nextError = rowErrors[owner.order]?.homeAddress || "";
      if (owner.homeAddressError !== nextError) {
        updateOwnerRow(owner.order, { homeAddressError: nextError });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempted, ssn, citizenship, taxId, ownershipPercentage, additionalOwners]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (pendingAddressLookups > 0) return;
    setSubmitAttempted(true);
    setServerError("");

    const { validationErrors, rowErrors } = validate();
    setErrors(validationErrors);
    setOwnerRowErrors(rowErrors);
    Object.entries(rowErrors).forEach(([order, errs]) => {
      if (errs.homeAddress) {
        updateOwnerRow(Number(order), { homeAddressError: errs.homeAddress });
      }
    });
    if (Object.keys(validationErrors).length > 0 || Object.keys(rowErrors).length > 0) return;

    const sanitizedSsn = ssn.replace(/-/g, "");
    const sanitizedTaxId = taxId.replace(/-/g, "");
    const ownersForState = hasAdditionalOwners
      ? additionalOwners.map((owner) => ({
          order: owner.order,
          email: owner.email,
          phone: "+1" + owner.phone.replace(/[^0-9]/g, ""),
          firstName: owner.firstName,
          lastName: owner.lastName,
          dob: owner.dob ? owner.dob.format("YYYY-MM-DD") : "",
          ssn: owner.ssn.replace(/-/g, ""),
          ownershipPercentage: Number(owner.ownershipPercentage),
          homeAddress: owner.homeAddress,
          homeAddressComponents: owner.homeAddressComponents,
          unitNumber: owner.unitNumber,
        }))
      : [];

    dispatch({
      type: "QUESTIONS",
      payload: { ...state.questions, ssn: sanitizedSsn, citizenship },
    });
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        taxId: sanitizedTaxId,
        ownershipPercentage,
        additionalOwners: ownersForState,
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

    setShowLoader(true);
    try {
      // Each field keeps its own autosave call, matching the payload shape
      // of its original standalone screen. These are independent fields, so
      // fire them concurrently instead of one round trip at a time.
      const requests = [
        axios.post(endpoint, { ssn_number: sanitizedSsn, citizenship }),
        // taxId.js's original POST guard (`state.questions.textId !== ""`)
        // referenced a field name that never exists, so it always evaluated
        // true — preserved here as an unconditional post.
        axios.post(endpoint, { tax_id: sanitizedTaxId }),
        axios.post(endpoint, { ownership_percentage: parseInt(ownershipPercentage) }),
        axios.post(endpoint, {
          additional_owners: ownersForState.map((owner) => {
            const resolvedAddress = helpers.extractAddressComponents(
              owner.homeAddressComponents,
              { state: "short", route: "short" },
              owner.unitNumber
            );
            return {
              order: owner.order,
              email: owner.email,
              phone_no: owner.phone,
              first_name: owner.firstName,
              last_name: owner.lastName,
              date_of_birth: owner.dob,
              ssn_number: owner.ssn,
              ownership_percentage: owner.ownershipPercentage,
              home_address: resolvedAddress !== false ? resolvedAddress : null,
            };
          }),
        }),
      ];
      await Promise.all(requests);
      goNext();
    } catch (error) {
      setServerError(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">Identity &amp; ownership</h2>
      <p className="pd6-step__helper">
        Everyone with a stake in the business has to be named before this can go to a lender.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepOwnershipSsn">
            <Form.Label>
              Social security number<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="tel"
              inputMode="numeric"
              placeholder="123-45-6789"
              value={ssn}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/\D/g, "");
                if (sanitized.length < 10) setSsn(formatDashed(sanitized, [3, 5]));
              }}
              isInvalid={submitAttempted && Boolean(errors.ssn)}
            />
            <Form.Control.Feedback type="invalid">{errors.ssn}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepOwnershipCitizenship">
            <Form.Label>
              Citizenship<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              {CITIZENSHIP_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip ${citizenship === option ? "pd6-chip--selected" : ""}`}
                  onClick={() => setCitizenship(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            {submitAttempted && errors.citizenship && (
              <div className="pd6-field-error">{errors.citizenship}</div>
            )}
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepOwnershipTaxId">
          <Form.Label>Tax ID</Form.Label>
          <Form.Control
            type="text"
            placeholder="12-3456789"
            autoComplete="off"
            value={taxId}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "");
              if (sanitized.length < 10) setTaxId(formatDashed(sanitized, [2]));
            }}
            isInvalid={submitAttempted && Boolean(errors.taxId)}
          />
          <Form.Control.Feedback type="invalid">{errors.taxId}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="pd6-field pd6-field--narrow" controlId="stepOwnershipPercentage">
          <Form.Label>
            Ownership Percentage<span className="pd6-required">*</span>
          </Form.Label>
          <div className="pd6-percent-input">
            <Form.Control
              type="tel"
              inputMode="numeric"
              placeholder="100"
              value={ownershipPercentage}
              onChange={handleOwnershipChange}
              isInvalid={submitAttempted && Boolean(errors.ownershipPercentage)}
            />
            <span className="pd6-percent-input__suffix">%</span>
          </div>
          <Form.Control.Feedback type="invalid" style={{ display: errors.ownershipPercentage ? "block" : "none" }}>
            {errors.ownershipPercentage}
          </Form.Control.Feedback>
          <div style={{ fontSize: "12px", color: "#8a8a92", marginTop: "6px", lineHeight: 1.45 }}>
            Whole numbers, 1 to 100.
          </div>
          {hasAdditionalOwners && (
            <div style={{ fontSize: "12px", color: "#8a8a92", marginTop: "6px", lineHeight: 1.45 }}>
              Remaining share: <strong>{totalUnassignedShare}%</strong> to assign
              {" "}
              — <strong>{totalAssignedShare}%</strong> assigned so far
            </div>
          )}
        </Form.Group>

        {hasAdditionalOwners &&
          additionalOwners.map((owner, index) => (
            <div className="pd6-reveal-panel" key={owner.order}>
              <div className="pd6-reveal-panel__header">
                <span className="pd6-reveal-panel__tag">
                  {index === 0 ? "Added automatically" : `Owner ${owner.order + 1} of the business`}
                </span>
                <span style={{ fontSize: "14px", fontWeight: 600 }}>
                  {index === 0 ? "The rest of the business — who holds it?" : "Another owner"}
                </span>
                <button
                  type="button"
                  className="pd6-btn pd6-btn--ghost pd6-btn--small"
                  onClick={() => removeOwnerRow(owner.order)}
                >
                  Remove
                </button>
              </div>

              <Form.Group className="pd6-field mt-3" controlId={`stepOwnershipOwnerShare-${owner.order}`}>
                <Form.Label>
                  This owner's share<span className="pd6-required">*</span>
                </Form.Label>
                <div className="pd6-percent-input">
                  <Form.Control
                    type="tel"
                    inputMode="numeric"
                    placeholder="0"
                    value={owner.ownershipPercentage}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                      updateOwnerRow(owner.order, {
                        ownershipPercentage:
                          digitsOnly === "" ? "" : Math.min(parseInt(digitsOnly, 10), 100),
                      });
                    }}
                    isInvalid={
                      submitAttempted && Boolean(ownerRowErrors[owner.order]?.ownershipPercentage)
                    }
                  />
                  <span className="pd6-percent-input__suffix">%</span>
                </div>
                <Form.Control.Feedback
                  type="invalid"
                  style={{
                    display: ownerRowErrors[owner.order]?.ownershipPercentage ? "block" : "none",
                  }}
                >
                  {ownerRowErrors[owner.order]?.ownershipPercentage}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerEmail-${owner.order}`}>
                <Form.Label>
                  Email<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  autoComplete="off"
                  placeholder="name@example.com"
                  value={owner.email}
                  onChange={(e) => updateOwnerRow(owner.order, { email: e.target.value })}
                  isInvalid={submitAttempted && Boolean(ownerRowErrors[owner.order]?.email)}
                />
                <Form.Control.Feedback type="invalid">
                  {ownerRowErrors[owner.order]?.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerPhone-${owner.order}`}>
                <Form.Label>
                  Phone<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  placeholder="(555)-555-5555"
                  value={owner.phone}
                  onChange={(e) => handleOwnerPhoneChange(owner.order, e)}
                  isInvalid={submitAttempted && Boolean(ownerRowErrors[owner.order]?.phone)}
                />
                <Form.Control.Feedback type="invalid">
                  {ownerRowErrors[owner.order]?.phone}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="pd6-field-row">
                <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerFirstName-${owner.order}`}>
                  <Form.Label>
                    First name<span className="pd6-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={owner.firstName}
                    onChange={(e) => updateOwnerRow(owner.order, { firstName: e.target.value })}
                    isInvalid={submitAttempted && Boolean(ownerRowErrors[owner.order]?.firstName)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {ownerRowErrors[owner.order]?.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerLastName-${owner.order}`}>
                  <Form.Label>
                    Last name<span className="pd6-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={owner.lastName}
                    onChange={(e) => updateOwnerRow(owner.order, { lastName: e.target.value })}
                    isInvalid={submitAttempted && Boolean(ownerRowErrors[owner.order]?.lastName)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {ownerRowErrors[owner.order]?.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerDob-${owner.order}`}>
                <Form.Label>
                  Date of birth<span className="pd6-required">*</span>
                </Form.Label>
                <SecondOwnerBirthday
                  value={owner.dob}
                  onChange={(value) => updateOwnerRow(owner.order, { dob: value })}
                />
                {submitAttempted && ownerRowErrors[owner.order]?.dob && (
                  <div className="pd6-field-error">{ownerRowErrors[owner.order].dob}</div>
                )}
              </Form.Group>

              <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerSsn-${owner.order}`}>
                <Form.Label>
                  SSN<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  placeholder="123-45-6789"
                  value={owner.ssn}
                  onChange={(e) => handleOwnerSSNChange(owner.order, e)}
                  isInvalid={submitAttempted && Boolean(ownerRowErrors[owner.order]?.ssn)}
                />
                <Form.Control.Feedback type="invalid">
                  {ownerRowErrors[owner.order]?.ssn}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="pd6-field" controlId={`stepOwnershipOwnerHomeAddress-${owner.order}`}>
                <CustomAddressAutocomplete
                  label="Home address"
                  addressSearchQuery={owner.homeAddress}
                  setAddressSearchQuery={(value) => updateOwnerRow(owner.order, { homeAddress: value })}
                  setAddressComponents={(components) =>
                    updateOwnerRow(owner.order, { homeAddressComponents: components })
                  }
                  addressValidationError={submitAttempted ? owner.homeAddressError : ""}
                  setAddressValidationError={(value) =>
                    updateOwnerRow(owner.order, { homeAddressError: value })
                  }
                  placeholder="home address"
                  localStorageLabel={`additionalOwnerHomeComponentsMazo_${owner.order}`}
                  isRequired={true}
                  onResolvingChange={handleAddressResolvingChange}
                />
                <Form.Control
                  type="text"
                  className="pd6-address-unit-input"
                  placeholder="Apt, suite, or unit (optional)"
                  autoComplete="off"
                  value={owner.unitNumber ?? ""}
                  onChange={(e) => updateOwnerRow(owner.order, { unitNumber: e.target.value })}
                />
              </Form.Group>
            </div>
          ))}

        {hasAdditionalOwners && (
          <div className="pd6-field mb-3" style={{ marginTop: "16px" }}>
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost pd6-btn--small pd6-btn--add-owner"
              onClick={addOwnerRow}
            >
              + Add another owner
            </button>
            {submitAttempted && errors.ownerShareSum && (
              <div className="pd6-field-error">{errors.ownerShareSum}</div>
            )}
            {submitAttempted && errors.additionalOwners && (
              <div className="pd6-field-error">{errors.additionalOwners}</div>
            )}
          </div>
        )}

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted &&
          (() => {
            const rowErrorFieldCount = Object.values(ownerRowErrors).reduce(
              (sum, errs) => sum + Object.keys(errs).length,
              0
            );
            const totalErrorCount = Object.keys(errors).length + rowErrorFieldCount;
            if (totalErrorCount === 0) return null;
            return (
              <div className="pd6-form-summary-error">
                {totalErrorCount} field{totalErrorCount === 1 ? "" : "s"} still need
                {totalErrorCount === 1 ? "s" : ""} an answer.
              </div>
            );
          })()}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button
              type="submit"
              className="pd6-btn pd6-btn--primary"
              disabled={showLoader || pendingAddressLookups > 0}
            >
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepOwnership;
